import React from "react";
import {
    getSunPosition,
    getMoonPosition,
} from "../../modules/getCelestialBodyPosition";
import { sunColourMap, gradients } from "../../modules/colors-and-stuff";
import { betweenTimes, getSunPhase } from "../../modules/time-stuff";
import * as d3 from "d3";

class Sundial extends React.Component {

    componentDidMount() {
        this.id = this.props.city.replace(/ /g, "");
        this.setup();
        this.draw();
    }

    componentWillUpdate(newProps) {
        this.draw();
    }

    render() {
        const { date } = this.props;
        const timeString = date.format("HH:mm");
        const dateString = date.format("dd, MMM DD");
        return (
            <div ref={r => (this.div = r)} className="sundial">
                <h3 className="sundial-text">{this.props.city}</h3>
                <h5 className="sundial-timezone">GMT{this.props.tz}</h5>
                <div className="sundial-container">
                    <svg className="sundial-svg" ref={r => (this.svg = r)}>
                        <defs>
                            <linearGradient id={"gradient-" + this.id} x1="0" x2="0" y1="0" y2="1">
                                <stop ref={r => this.gradientStartStop = r} offset="0%"/>
                                <stop ref={r => this.gradientEndStop = r} offset="100%"/>
                            </linearGradient>
                        </defs>
                        <rect ref={r => (this.svgBackground = r)} />
                        <line ref={r => (this.lineShadow = r)} />
                        <image
                            ref={r => (this.silhouetteShadow = r)}
                            xlinkHref={this.props.shadowImage}
                            height="100"
                            width="100"
                        />
                        <circle ref={r => (this.figure = r)} />
                        <circle ref={r => (this.sun = r)} />
                        <circle ref={r => (this.moon = r)} />
                    </svg>
                    <div
                        ref={r => (this.stars = r)}
                        className="sundial-stars"
                    />
                </div>
                <h4 className="sundial-text">{timeString}</h4>
                <h5 className="sundial-text">{dateString}</h5>
            </div>
        );
    }

    setup() {
        this.width = 420;
        this.height = 420;

        d3
            .select(this.svg)
            .attr("width", this.width)
            .attr("height", this.height)
            .attr("viewBox", `0 0 ${this.width} ${this.height}`);

        this.center = {
            x: this.width / 2,
            y: this.height / 2,
        };
    }

    draw() {
        const { date, coords } = this.props;

        // Sun should touch the edge.
        const sunDistancePx = this.center.x * 1.414 * 1.2;
        const moonDistancePx = this.center.x * 1.414;
        const sunPos = getSunPosition(date.toDate(), coords.lat, coords.lng);
        const sunPhase = getSunPhase(date, sunPos);
        const moonPos = getMoonPosition(date.toDate(), coords.lat, coords.lng);

        const moonDistanceScale = d3
            .scaleLinear()
            .domain([-Math.PI / 2, Math.PI / 2])
            .range([0, moonDistancePx]);
        const shadowLengthScale = d3
            .scaleLinear()
            .domain([-Math.PI / 2, Math.PI / 2])
            .range([2, 0]);
        const sunDistanceScale = d3
            .scaleLinear()
            .domain([-Math.PI / 2, Math.PI / 2])
            .range([sunDistancePx, 0]);
        const sunSizeScale = d3
            .scaleLinear()
            .domain([-Math.PI / 2, Math.PI / 2])
            .range([-15, 42]);
        const moonSizeScale = d3
            .scaleLinear()
            .domain([Math.PI / 2, -Math.PI / 2])
            .range([5, 16]);
        const discShadowSizeScale = d3
            .scaleLinear()
            .domain([0, Math.PI / 2])
            .range([10 * 1.414, 0]);
        const shadowOpacityScale = d3
            .scaleLinear()
            .domain([0, Math.PI / 2])
            .range([0.05, 0.2])
            .clamp(true);

        const angleOffset = Math.PI / 2;
        const getSunX = pos => {
            return (
                this.center.x +
                Math.cos(pos.azimuth + angleOffset) * sunDistanceScale(pos.altitude)
            );
        };
        const getSunY = pos => {
            return (
                this.center.y +
                Math.sin(pos.azimuth + angleOffset) * sunDistanceScale(pos.altitude)
            );
        };

        const getMoonX = pos => {
            return (
                this.center.x +
                Math.cos(pos.azimuth + angleOffset) * moonDistanceScale(pos.altitude)
            );
        };
        const getMoonY = pos => {
            return (
                this.center.y +
                Math.sin(pos.azimuth + angleOffset) * moonDistanceScale(pos.altitude)
            );
        };

        const getShadowX = pos => {
            return (
                this.center.x +
                Math.cos(pos.azimuth - angleOffset) * (shadowLengthScale(pos.altitude) * 150)
            );
        };
        const getShadowY = pos => {
            return (
                this.center.y +
                Math.sin(pos.azimuth - angleOffset) * (shadowLengthScale(pos.altitude) * 150)
            );
        };

        d3.select(this.svgBackground)
            .style("fill", `url(#gradient-${this.id})`)
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", this.width)
            .attr("height", this.width);

        d3.select(this.gradientStartStop)
        .style("stop-color", gradients[sunPhase][0]);

        d3.select(this.gradientEndStop)
        .style("stop-color", gradients[sunPhase][1]);

        const shadowOpacity = shadowOpacityScale(sunPos.altitude);

        d3
            .select(this.lineShadow)
            .style("visibility", this.props.shadowImage ? 'hidden' : 'visible')
            .attr("x1", this.center.x)
            .attr("y1", this.center.y)
            .style("stroke-width", 20)
            .style("stroke-linecap", "round")
            .style("stroke", "rgb(22,22,30)")
            .style("opacity", sunPhase === "night" ? 0 : shadowOpacity)
            .attr("x2", getShadowX(sunPos))
            .attr("y2", getShadowY(sunPos));

        const shadowSize = sunDistancePx / 2.5;
        const shadowAngle = (sunPos.azimuth + angleOffset) * (180 / Math.PI) - 90;
        const lengthMult = shadowLengthScale(sunPos.altitude);
        d3
            .select(this.silhouetteShadow)
            .style("display", this.props.shadowImage ? true : false)
            .attr("class", "sundial-silhouette")
            .attr("x", this.center.x - shadowSize / 2)
            .attr("y", this.center.y - shadowSize)
            .attr("width", shadowSize)
            .attr("height", shadowSize)
            .style(
                "transform",
                `rotate(${shadowAngle}deg) scaleY(${lengthMult})`
            )
            .style("transform-origin", `${this.center.x}px ${this.center.y}px`)
            .transition()
            .style("opacity", sunPhase === "night" ? 0 : shadowOpacity);
        
        d3
            .select(this.sun)
            .attr("r", Math.max(0, sunSizeScale(sunPos.altitude)))
            .attr("cx", getSunX(sunPos))
            .attr("cy", getSunY(sunPos))
            .transition()
            .duration(200)
            .style("opacity", d => (sunPhase === "night" ? 0.2 : 1.0))
            .style("fill", d => sunColourMap[sunPhase] || "red");

        d3
            .select(this.moon)
            .attr("r", Math.max(0, moonSizeScale(moonPos.altitude)))
            .attr("cx", getMoonX(moonPos))
            .attr("cy", getMoonY(moonPos))
            .style("fill", "white")
            .transition()
            .style("opacity", sunPhase === "night" ? 1.0 : 0.2);

        d3
            .select(this.figure)
            .attr("r", 18)
            .attr("cx", this.center.x)
            .attr("cy", this.center.y)
            .style("fill", "#333");

        const starOpacity =
            sunPhase === "night" ? 0.75 : sunPhase === "day" ? 0 : 0.1;
        d3.select(this.stars).style("opacity", starOpacity);

        const targetShadowLength = discShadowSizeScale(sunPos.altitude);
        const xOffset = Math.cos(sunPos.azimuth + angleOffset) * -targetShadowLength;
        const yOffset = Math.sin(sunPos.azimuth + angleOffset) * -targetShadowLength;
        const discShadowOpacty = sunPhase === 'night' ? 0 : 0.05;
        d3
            .select(this.svg)
            .style(
                "box-shadow",
                `${xOffset}px ${yOffset}px rgba(0, 0, 0, ${discShadowOpacty})`
            );
    }
}

export default Sundial;



// WEBPACK FOOTER //
// ./src/components/sundial/sundial.js